import moment from 'moment-timezone';

/**
 * Returns true when the manual ticket time field has no usable value.
 */
export function isManualTicketTimeMissing(time) {
  return time == null || time === undefined || String(time).trim() === '';
}

/**
 * MUI TimePicker changes use "today" as the date. Re-apply the picked clock
 * time onto an anchor datetime (existing wait slot or ticket created date)
 * so yesterday's tickets keep yesterday's date.
 *
 * @param {any} pickedValue - value from TimePicker onChange
 * @param {...any} anchorCandidates - prefer first valid (existing slot, ticket date, …)
 * @returns {string} moment().format() ISO string
 */
export function applyPickedTimeToAnchorDate(pickedValue, ...anchorCandidates) {
  const picked = moment(pickedValue);
  if (!picked.isValid()) {
    return moment().format();
  }

  let anchor = null;
  for (const candidate of anchorCandidates) {
    if (candidate == null || candidate === '' || String(candidate) === 'Invalid date') {
      continue;
    }
    const m = moment(candidate);
    if (m.isValid()) {
      anchor = m;
      break;
    }
  }
  if (!anchor) {
    anchor = moment();
  }

  return anchor
    .clone()
    .hour(picked.hour())
    .minute(picked.minute())
    .second(picked.second())
    .millisecond(0)
    .format();
}

/** Best-effort ticket calendar date for wait/start/end anchors. */
export function resolveTicketAnchorDate(ticketLike) {
  if (!ticketLike || typeof ticketLike !== 'object') return null;
  return (
    ticketLike.date_created ||
    ticketLike.tk_datecreated ||
    ticketLike.tk_timecreated ||
    null
  );
}

/**
 * Returns true when the manual ticket date field has no usable value.
 */
export function isManualTicketDateMissing(date) {
  return date == null || date === undefined || String(date).trim() === '';
}

/**
 * Reads the value from a native input element or NodeList.
 * Returns the first non-empty value found.
 */
function getInputValue(inputOrInputs) {
  if (!inputOrInputs) return '';
  const isNodeList = typeof NodeList !== 'undefined' && inputOrInputs instanceof NodeList;
  const inputs = isNodeList || Array.isArray(inputOrInputs)
    ? Array.from(inputOrInputs)
    : [inputOrInputs];

  for (const el of inputs) {
    if (el && String(el.value ?? '').trim()) {
      return String(el.value).trim();
    }
  }
  return '';
}

/**
 * Prefer the native time input value when React state is empty but the DOM has a value.
 */
export function resolveManualTicketTime(stateTime, inputEl) {
  const domValue = getInputValue(inputEl);
  if (domValue) return domValue;
  return isManualTicketTimeMissing(stateTime) ? '' : String(stateTime).trim();
}

/**
 * Prefer the native date input value when React state is empty but the DOM has a value.
 */
export function resolveManualTicketDate(stateDate, inputEl) {
  const domValue = getInputValue(inputEl);
  if (domValue) return domValue;
  return isManualTicketDateMissing(stateDate) ? '' : String(stateDate).trim();
}

/**
 * Compare truck and driver company using comp_acct when available, otherwise comp_name.
 */
export function ticketTruckDriverCompanyMismatch(driver, truck) {
  if (!driver || !truck) return false;

  const driverKey = driver.comp_acct ?? driver.comp_name?.trim()?.toLowerCase();
  const truckKey  = truck.comp_acct  ?? truck.comp_name?.trim()?.toLowerCase();

  if (!driverKey || !truckKey) return false;
  return String(driverKey) !== String(truckKey);
}

/**
 * Syncs ticket date/time fields from their native inputs before validation/submit.
 * Uses data-ticket-field-id and data-ticket-field-type attributes.
 */
export function syncTicketTimeFieldsFromDom(fields, selectorPrefix = 'data-ticket-field-id') {
  return fields.map((field) => {
    const timeInputs = document.querySelectorAll(
      `[${selectorPrefix}="${field.id}"][data-ticket-field-type="time"]`
    );
    const dateInputs = document.querySelectorAll(
      `[${selectorPrefix}="${field.id}"][data-ticket-field-type="date"]`
    );

    const time = resolveManualTicketTime(field.time, timeInputs);
    const date = resolveManualTicketDate(field.date, dateInputs);

    if (time === field.time && date === field.date) return field;
    return { ...field, time, date };
  });
}
